import { useState, type ReactNode } from "react";

import BybPerformanceChip from "@Components/common/byb-ds/byb-performance-chip";
import DecisionScoreInfoDialog from "@Components/game/session-results/decision/decision-score-info-dialog";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import { AnalyticsEvents } from "@Enums/index";
import MetricFilterTypes from "@Enums/MetricFilterTypes";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { buildDecisionScoreRows, getDecisionFeedbackMessage } from "@hooks/common/session/decision-score-rows";
import {
  getDisplayedDecisionOptions,
  getSelectedDecisionOption,
} from "@hooks/common/session/replay-decision-utils";
import { SessionFeedbackModel } from "@hooks/game/activity/session/use-session-feedback-model";
import useI18n from "@i18n";
import { Box, IconButton, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";

interface IDecisionScorePanel {
  feedbackModel: SessionFeedbackModel;
}

interface IAnimatedPanelShell {
  children: ReactNode;
  isExpanded: boolean;
}

interface IPanelHeader {
  title: string;
  isDisabled?: boolean;
  onInfoClick?: () => void;
}

interface IPanelFrame {
  children: ReactNode;
}

const PANEL_WIDTH = 320;
const PANEL_ANIMATION = {
  duration: 0.3,
  ease: "easeInOut",
} as const;
const PANEL_MOTION_STYLE = {
  overflow: "hidden",
  flexShrink: 0,
  display: "flex",
  alignSelf: "stretch",
} as const;

const AnimatedPanelShell = ({ children, isExpanded }: IAnimatedPanelShell) => {
  return (
    <AnimatePresence initial={false}>
      {isExpanded && (
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: PANEL_WIDTH, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={PANEL_ANIMATION}
          style={PANEL_MOTION_STYLE}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const PanelFrame = ({ children }: IPanelFrame) => {
  return (
    <Box
      sx={{
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
        backgroundColor: "dsSurface.v100",
        borderRadius: 3,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        border: theme => `1px solid ${theme.palette.dsSurface.v300}`,
        overflow: "hidden",
      }}
    >
      {children}
    </Box>
  );
};

const PanelHeader = ({ title, isDisabled = false, onInfoClick }: IPanelHeader) => {
  const theme = useTheme();

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="text1xl500" color="text.secondary">
        {title}
      </Typography>

      {onInfoClick && !isDisabled ? (
        <HapticsButtonWrapper>
          <IconButton
            size="medium"
            disableRipple
            onClick={onInfoClick}
            sx={{
              color: theme.palette.dsNeutral.v300,
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
                color: theme.palette.dsNeutral.v200,
              },
            }}
          >
            <FontAwesomeIcon icon={faCircleInfo} size="sm" />
          </IconButton>
        </HapticsButtonWrapper>
      ) : (
        <IconButton
          size="small"
          disableRipple
          disabled={isDisabled}
          onClick={onInfoClick}
          sx={{
            color: theme.palette.dsNeutral.v300,
            padding: 0,
            "&:hover": isDisabled
              ? undefined
              : {
                  backgroundColor: "transparent",
                  color: theme.palette.dsNeutral.v200,
                },
          }}
        >
          <FontAwesomeIcon icon={faCircleInfo} size="sm" />
        </IconButton>
      )}
    </Stack>
  );
};

const DecisionScorePanelLoading = ({ isExpanded }: { isExpanded: boolean }) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const scoreRowLabels = [
    i18n("session-results.decision-panel.expected-goals"),
    i18n("session-results.decision-panel.expected-threat"),
    i18n("session-results.decision-panel.expected-pass"),
    i18n("session-results.decision-panel.expected-pass-turnover"),
  ];

  return (
    <AnimatedPanelShell isExpanded={isExpanded}>
      <PanelFrame>
        <Stack spacing={3} padding={2.5} height="100%">
          <PanelHeader title={i18n("session-results.decision-panel.title")} isDisabled />

          <Stack direction="row" justifyContent="space-between" alignItems="center" minHeight={36}>
            <Skeleton variant="rounded" width={72} height={36} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={84} height={34} sx={{ borderRadius: 2 }} />
          </Stack>

          <Stack flex={1}>
            {scoreRowLabels.map((label, index) => (
              <Stack
                key={label}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                paddingY={1.75}
                borderBottom={index < scoreRowLabels.length - 1 ? `1px solid ${theme.palette.dsSurface.v300}` : "none"}
              >
                <Typography variant="textBase400" color="text.secondary">
                  {label}
                </Typography>

                <Skeleton variant="rounded" width={56} height={20} sx={{ borderRadius: 1 }} />
              </Stack>
            ))}
          </Stack>

          <Skeleton
            variant="rounded"
            width="100%"
            height={104}
            sx={{
              borderRadius: 2,
              marginTop: "auto",
            }}
          />
        </Stack>
      </PanelFrame>
    </AnimatedPanelShell>
  );
};

const DecisionScorePanel = ({ feedbackModel }: IDecisionScorePanel) => {
  const { i18n } = useI18n();
  const { track } = useAnalytics();
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const { replay, playback } = feedbackModel;

  const handleOpenInfoDialog = () => {
    setIsInfoDialogOpen(true);
    const eventData = {
      ...(feedbackModel.analytics.scenario ?? {}),
    };

    track(AnalyticsEvents.decision_score_info_popup_opened, eventData);
  };

  if (replay.areDecisionsLoading) {
    return <DecisionScorePanelLoading isExpanded={playback.isDecisionPanelExpanded} />;
  }

  if (replay.decisions.length <= 0) {
    return null;
  }

  const currentDecision = replay.decisions[playback.highlightPointIndex];
  if (!currentDecision) {
    return null;
  }

  const { maxScore } = getDisplayedDecisionOptions(currentDecision);
  const selectedOption = getSelectedDecisionOption(currentDecision, playback.selectedCard);
  const scoreRows = buildDecisionScoreRows(selectedOption, i18n);
  const currentScore = selectedOption?.displayScore ?? 0;
  const scorePercentage = maxScore > 0 ? Math.round((currentScore / maxScore) * 100) : 0;

  return (
    <>
      <AnimatedPanelShell isExpanded={playback.isDecisionPanelExpanded}>
        <PanelFrame>
          <Stack spacing={3} padding={2.5} height="100%">
            <PanelHeader title={i18n("session-results.decision-panel.title")} onInfoClick={handleOpenInfoDialog} />

            {selectedOption ? (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} minHeight={36}>
                  <Stack direction="row" alignItems="baseline" gap={0.5}>
                    <Typography variant="text3xl700" color="dsBase.v200">
                      {scorePercentage}
                    </Typography>
                    <Typography variant="text1xl600" color="text.secondary">
                      %
                    </Typography>
                  </Stack>

                  <BybPerformanceChip score={scorePercentage} metricKey={MetricFilterTypes.DECISION} />
                </Stack>

                <Stack flex={1}>
                  {scoreRows.map(({ label, value }, index) => (
                    <Stack
                      key={label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      paddingY={1.75}
                      borderBottom={index < scoreRows.length - 1 ? theme => `1px solid ${theme.palette.dsSurface.v300}` : "none"}
                    >
                      <Typography variant="textBase400" color="text.secondary">
                        {label}
                      </Typography>

                      <Typography variant="textBase600" color="dsBase.v200">
                        {value}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Box
                  sx={{
                    marginTop: "auto",
                    padding: 2,
                    backgroundColor: "dsSurface.v300",
                    borderRadius: 2,
                    border: theme => `1px solid ${theme.palette.dsSurface.v300}`,
                  }}
                >
                  <Typography variant="textBase400" color="text.secondary" textAlign="center">
                    {getDecisionFeedbackMessage(scorePercentage, i18n)}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  marginTop: "auto",
                  padding: 2,
                  backgroundColor: "dsSurface.v300",
                  borderRadius: 2,
                  border: theme => `1px solid ${theme.palette.dsSurface.v300}`,
                }}
              >
                <Typography variant="textBase400" color="text.secondary" textAlign="center">
                  {i18n("session-results.decision-panel.no-selection")}
                </Typography>
              </Box>
            )}
          </Stack>
        </PanelFrame>
      </AnimatedPanelShell>

      <DecisionScoreInfoDialog open={isInfoDialogOpen} onClose={() => setIsInfoDialogOpen(false)} />
    </>
  );
};

export default DecisionScorePanel;
